import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import styled, { keyframes, css } from 'styled-components'
import { toast } from 'react-toastify'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import AddRoundedIcon         from '@mui/icons-material/AddRounded'
import EditRoundedIcon        from '@mui/icons-material/EditRounded'
import DeleteRoundedIcon      from '@mui/icons-material/DeleteRounded'
import SearchRoundedIcon      from '@mui/icons-material/SearchRounded'
import CloseRoundedIcon       from '@mui/icons-material/CloseRounded'
import ElevatorRoundedIcon    from '@mui/icons-material/ElevatorRounded'
import BoltRoundedIcon        from '@mui/icons-material/BoltRounded'
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded'
import AcUnitRoundedIcon      from '@mui/icons-material/AcUnitRounded'
import WhatshotRoundedIcon    from '@mui/icons-material/WhatshotRounded'
import CompressRoundedIcon    from '@mui/icons-material/CompressRounded'
import WaterDropRoundedIcon   from '@mui/icons-material/WaterDropRounded'
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded'
import ComplianceShell from 'features/compliance/ComplianceShell'
import InfiniteScrollSentinel from 'components/InfiniteScrollSentinel'
import SearchableSelect from 'components/SearchableSelect'
import {
  listAssets, detectAssets, createAsset, updateAsset, deleteAsset,
} from 'services/assetService'
import AssetRegisterExplainer from './AssetRegisterExplainer'

const TYPE_ICON = {
  lift:            ElevatorRoundedIcon,
  generator:       BoltRoundedIcon,
  boiler:          WhatshotRoundedIcon,
  pressure_system: CompressRoundedIcon,
  fire_system:     LocalFireDepartmentRoundedIcon,
  hvac:            AcUnitRoundedIcon,
  water_system:    WaterDropRoundedIcon,
}
const TYPE_TINT = {
  lift:            '#33becc',
  generator:       '#d97706',
  boiler:          '#b00020',
  pressure_system: '#7C5FE0',
  fire_system:     '#D24724',
  hvac:            '#2aa6b3',
  water_system:    '#0e7490',
}

const PAGE = 20

const humanize = key => (key || '').replace(/[_-]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase())

const formatDate = iso => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

const SORT_VALUE = {
  assetTypeLabel: a => (a.assetTypeLabel || a.assetType || '').toLowerCase(),
  name:           a => (a.name || '').toLowerCase(),
  location:       a => (a.location || '').toLowerCase(),
  filesCount:     a => a.filesCount ?? 0,
  createdAt:      a => (a.createdAt ? new Date(a.createdAt).getTime() : 0),
}

const AssetRegisterPage = ({ bpId: bpIdProp, embedded = false, onTabChange, chromeless = false } = {}) => {
  const routeParams = useParams()
  const bpId = bpIdProp ?? routeParams.bpId
  const [loading, setLoading] = useState(true)
  const [assets, setAssets]   = useState([])
  const [types, setTypes]     = useState([])
  const [detecting, setDetecting] = useState(false)
  const [editing, setEditing] = useState(null)
  const [justScanned, setJustScanned] = useState(false)

  const [search, setSearch]       = useState('')
  const [sortKey, setSortKey]     = useState('assetTypeLabel')
  const [sortDir, setSortDir]     = useState('asc')
  const [typeFilter, setTypeFilter] = useState([])
  const [docFilter, setDocFilter] = useState('all')
  const [visibleCount, setVisibleCount] = useState(PAGE)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await listAssets(bpId)
      const payload = res?.data?.data || {}
      const found = payload.assets || []
      setAssets(found)
      setTypes(payload.assetTypes || [])
      if (found.length) setJustScanned(false)
    } catch (e) {
      toast.error('Could not load asset register.')
    } finally {
      setLoading(false)
    }
  }, [bpId])

  useEffect(() => { load() }, [load])

  const runDetect = async () => {
    setDetecting(true)
    try {
      await detectAssets(bpId)
      setJustScanned(true)
      toast.info('Scan started — checking your documents. This can take up to a minute.')
    } catch (e) {
      toast.error('Could not start asset detection.')
    } finally {
      setDetecting(false)
    }
  }

  const handleDelete = async asset => {
    if (!window.confirm(`Delete ${asset.name}? Linked files will be unlinked but not deleted.`)) return
    try {
      await deleteAsset(bpId, asset.id)
      toast.success(`Removed ${asset.name}.`)
      await load()
    } catch (e) {
      toast.error('Delete failed.')
    }
  }

  const typeOptions = useMemo(() => {
    const map = new Map()
    assets.forEach(a => {
      const entry = map.get(a.assetType) || {
        value: a.assetType,
        label: a.assetTypeLabel || humanize(a.assetType),
        count: 0,
      }
      entry.count += 1
      map.set(a.assetType, entry)
    })
    return Array.from(map.values()).sort((x, y) => x.label.localeCompare(y.label))
  }, [assets])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    const rows = assets.filter(a => {
      if (typeFilter.length && !typeFilter.includes(a.assetType)) return false
      if (docFilter === 'with' && !(a.filesCount > 0)) return false
      if (docFilter === 'without' && a.filesCount > 0) return false
      if (!q) return true
      const hay = [a.name, a.assetTypeLabel, a.assetType, a.location, a.description]
        .filter(Boolean).join(' ').toLowerCase()
      return hay.includes(q)
    })
    const pick = SORT_VALUE[sortKey] || SORT_VALUE.name
    const dir = sortDir === 'asc' ? 1 : -1
    return rows.sort((a, b) => {
      const va = pick(a)
      const vb = pick(b)
      if (va < vb) return -1 * dir
      if (va > vb) return 1 * dir
      return (a.name || '').localeCompare(b.name || '')
    })
  }, [assets, search, typeFilter, docFilter, sortKey, sortDir])

  const visible = useMemo(() => filtered.slice(0, visibleCount), [filtered, visibleCount])

  useEffect(() => { setVisibleCount(PAGE) }, [search, sortKey, sortDir, typeFilter, docFilter])

  const toggleSort = key => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const toggleType = value => {
    setTypeFilter(cur => (cur.includes(value) ? cur.filter(v => v !== value) : [...cur, value]))
  }

  const filtersActive = Boolean(search.trim()) || typeFilter.length > 0 || docFilter !== 'all'
  const clearFilters = () => { setSearch(''); setTypeFilter([]); setDocFilter('all') }

  const columns = [
    { key: 'assetTypeLabel', label: 'Type', align: 'left' },
    { key: 'name', label: 'Name', align: 'left' },
    { key: 'location', label: 'Where it is', align: 'left' },
    { key: 'filesCount', label: 'Documents', align: 'center', numeric: true },
    { key: 'createdAt', label: 'Added', align: 'left' },
  ]

  const sortHint = key => {
    if (sortKey !== key) return <SortIdle aria-hidden='true'>↕</SortIdle>
    return <SortActive aria-hidden='true'>{sortDir === 'asc' ? '▲' : '▼'}</SortActive>
  }

  return (
    <ComplianceShell activeTab='assets' bpId={bpId} embedded={embedded} onTabChange={onTabChange} chromeless={chromeless}>
      <Wrap>
        <Head>
          <HeadText>
            <Eyebrow>Asset register</Eyebrow>
            <Title data-tour-id='assets.title'>What this building contains</Title>
            <Subtitle>
              Detected automatically from your uploaded O&amp;M manuals, certificates and inspection reports — and you can
              add anything yourself. Each item keeps its own history of certificates and inspections, and warns you before
              they run out.
            </Subtitle>
            <ExplainerRow data-tour-id='assets.explainer'>
              <AssetRegisterExplainer />
            </ExplainerRow>
          </HeadText>
          <HeadActions>
            <DetectButton
              onClick={runDetect}
              disabled={detecting}
              title="Scans your uploaded documents (O&Ms, certificates, inspection reports) and lists the building's equipment as assets. It only adds new items it finds — it never changes or removes anything you've already recorded."
            >
              <AutoAwesomeRoundedIcon style={{ fontSize: 16 }} />
              {detecting ? 'Scanning…' : 'AI detect'}
            </DetectButton>
            <AddButton onClick={() => setEditing({ assetType: 'lift', name: '', location: '', description: '' })}>
              <AddRoundedIcon style={{ fontSize: 16 }} />
              Add manually
            </AddButton>
          </HeadActions>
        </Head>

        {justScanned && (
          <ScanNotice>
            <AutoAwesomeRoundedIcon style={{ fontSize: 18 }} />
            <ScanText>
              <strong>Scan started.</strong> We’re checking your documents in the background — this can take up to a
              minute. AI detect only <strong>adds</strong> equipment; it never removes anything.
            </ScanText>
            <RefreshBtn type='button' onClick={load}>Check now</RefreshBtn>
          </ScanNotice>
        )}

        {loading ? (
          <TableSkeleton />
        ) : assets.length === 0 ? (
          <EmptyPanel>
            <h3>No equipment listed yet</h3>
            <p>
              This register builds itself from your <strong>asset documents</strong> — lift inspections, boiler
              services, fire-system certificates and the like. <strong>AI detect</strong> reads those and lists the
              equipment it finds.
            </p>
            <p>
              Seeing nothing after a scan? It almost always means those documents haven’t been uploaded or classified
              yet — <strong>not</strong> that anything was removed. AI detect only ever <strong>adds</strong> equipment;
              it never deletes.
            </p>
            <p>
              Press <strong>AI detect</strong> to scan again, or <strong>Add manually</strong> if you already know
              what’s in the building.
            </p>
          </EmptyPanel>
        ) : (
          <>
            <Toolbar>
              <SearchWrap>
                <SearchRoundedIcon style={{ fontSize: 18 }} />
                <SearchInput
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder='Search by name, type, location, make, model or serial number…'
                  aria-label='Search assets'
                  data-tour-id='assets.search'
                />
                {search && (
                  <ClearSearch type='button' onClick={() => setSearch('')} aria-label='Clear search' title='Clear search'>
                    <CloseRoundedIcon style={{ fontSize: 16 }} />
                  </ClearSearch>
                )}
              </SearchWrap>
            </Toolbar>

            <FilterRow>
              <FilterLabel>Type</FilterLabel>
              <Chips>
                {typeOptions.map(opt => {
                  const Icon = TYPE_ICON[opt.value] || InsertDriveFileRoundedIcon
                  const active = typeFilter.includes(opt.value)
                  return (
                    <Chip
                      key={opt.value}
                      type='button'
                      $active={active}
                      aria-pressed={active}
                      onClick={() => toggleType(opt.value)}
                      title={`Show only ${opt.label} — ${opt.count} item${opt.count === 1 ? '' : 's'}`}
                    >
                      <Icon style={{ fontSize: 14, color: TYPE_TINT[opt.value] || '#7a7a83' }} />
                      {opt.label}
                      <ChipCount>{opt.count}</ChipCount>
                    </Chip>
                  )
                })}
              </Chips>
            </FilterRow>

            <FilterRow>
              <FilterLabel>Documents</FilterLabel>
              <Chips>
                <Chip type='button' $active={docFilter === 'all'} aria-pressed={docFilter === 'all'}
                  onClick={() => setDocFilter('all')} title='Show every asset, whether or not it has documents on file'>
                  All
                </Chip>
                <Chip type='button' $active={docFilter === 'with'} aria-pressed={docFilter === 'with'}
                  onClick={() => setDocFilter('with')} title='Show only assets that already have at least one document on file'>
                  Has documents
                </Chip>
                <Chip type='button' $active={docFilter === 'without'} aria-pressed={docFilter === 'without'}
                  onClick={() => setDocFilter('without')} title='Show only assets with no documents yet — these may need a certificate or inspection uploaded'>
                  Needs documents
                </Chip>
              </Chips>
            </FilterRow>

            <CountRow>
              <CountText>
                Showing <strong>{visible.length}</strong> of <strong>{filtered.length}</strong>{' '}
                asset{filtered.length === 1 ? '' : 's'}
                {filtersActive && filtered.length !== assets.length && ` (filtered from ${assets.length})`}
              </CountText>
              {filtersActive && (
                <ClearFilters type='button' onClick={clearFilters}>Clear search and filters</ClearFilters>
              )}
            </CountRow>

            {filtered.length === 0 ? (
              <NoResults>
                <h3>No matching assets</h3>
                <p>Nothing here matches your search or filters. Try a different word, or clear them to see everything again.</p>
                <ClearFilters type='button' onClick={clearFilters}>Clear search and filters</ClearFilters>
              </NoResults>
            ) : (
              <TableScroll>
                <Table>
                  <thead>
                    <tr>
                      {columns.map(col => (
                        <Th
                          key={col.key}
                          scope='col'
                          $align={col.align}
                          $numeric={col.numeric}
                          onClick={() => toggleSort(col.key)}
                          aria-sort={sortKey === col.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                          title={`Sort by ${col.label.toLowerCase()}`}
                        >
                          <ThInner $align={col.align}>
                            {col.label}
                            {sortHint(col.key)}
                          </ThInner>
                        </Th>
                      ))}
                      <Th scope='col' $align='right' aria-hidden='true'>Actions</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map(a => {
                      const Icon = TYPE_ICON[a.assetType] || InsertDriveFileRoundedIcon
                      const tint = TYPE_TINT[a.assetType] || '#7a7a83'
                      return (
                        <Row key={a.id}>
                          <Td>
                            <TypeCell>
                              <TypeTile $tint={tint}><Icon style={{ fontSize: 16 }} /></TypeTile>
                              <TypeLabel>{a.assetTypeLabel || humanize(a.assetType)}</TypeLabel>
                            </TypeCell>
                          </Td>
                          <Td>
                            <NameCell>
                              <NameText>{a.name}</NameText>
                              {a.description && <DescText title={a.description}>{a.description}</DescText>}
                            </NameCell>
                          </Td>
                          <Td>{a.location ? <LocationText>{a.location}</LocationText> : <Muted>Not recorded</Muted>}</Td>
                          <Td $align='center'>
                            <FileChip
                              $none={!(a.filesCount > 0)}
                              title={a.filesCount > 0
                                ? `${a.filesCount} document${a.filesCount === 1 ? '' : 's'} on file for this asset`
                                : 'No documents on file yet — you may need to upload a certificate or inspection'}
                            >
                              <InsertDriveFileRoundedIcon style={{ fontSize: 12 }} />
                              {a.filesCount} file{a.filesCount === 1 ? '' : 's'}
                            </FileChip>
                          </Td>
                          <Td>
                            <DateText title={a.createdAt ? `Added on ${formatDate(a.createdAt)}` : 'Date added not recorded'}>
                              {formatDate(a.createdAt)}
                            </DateText>
                          </Td>
                          <Td $align='right'>
                            <RowActions>
                              <IconBtn onClick={() => setEditing({ ...a })} title={`Edit ${a.name}`} aria-label={`Edit ${a.name}`}>
                                <EditRoundedIcon style={{ fontSize: 16 }} />
                              </IconBtn>
                              <IconBtn onClick={() => handleDelete(a)} title={`Delete ${a.name}`} aria-label={`Delete ${a.name}`}>
                                <DeleteRoundedIcon style={{ fontSize: 16 }} />
                              </IconBtn>
                            </RowActions>
                          </Td>
                        </Row>
                      )
                    })}
                  </tbody>
                </Table>
              </TableScroll>
            )}

            <InfiniteScrollSentinel
              hasMore={visibleCount < filtered.length}
              loading={false}
              onLoadMore={() => setVisibleCount(c => c + PAGE)}
            />
          </>
        )}

        {editing && (
          <AssetEditModal
            asset={editing}
            types={types}
            onClose={() => setEditing(null)}
            onSave={async patch => {
              try {
                if (editing.id) await updateAsset(bpId, editing.id, patch)
                else await createAsset(bpId, patch)
                toast.success(editing.id ? 'Asset updated.' : 'Asset added.')
                setEditing(null)
                await load()
              } catch (e) {
                toast.error('Save failed.')
              }
            }} />
        )}
      </Wrap>
    </ComplianceShell>
  )
}

const TableSkeleton = () => (
  <SkeletonWrap aria-busy='true' aria-label='Loading asset register'>
    <SkeletonToolbar>
      <SkeletonBar $w='320px' $h='40px' $r='10px' />
    </SkeletonToolbar>
    <SkeletonChips>
      {Array.from({ length: 5 }).map((_, i) => <SkeletonBar key={i} $w='96px' $h='30px' $r='999px' />)}
    </SkeletonChips>
    <SkeletonTableBox>
      {Array.from({ length: 8 }).map((_, i) => (
        <SkeletonRow key={i} style={{ animationDelay: `${i * 55}ms` }}>
          <SkeletonBar $w='34px' $h='34px' $r='9px' />
          <SkeletonBar $w='26%' $h='12px' />
          <SkeletonBar $w='18%' $h='12px' />
          <SkeletonBar $w='56px' $h='20px' $r='999px' />
          <SkeletonBar $w='72px' $h='12px' />
        </SkeletonRow>
      ))}
    </SkeletonTableBox>
  </SkeletonWrap>
)

const AssetEditModal = ({ asset, types, onClose, onSave }) => {
  const [draft, setDraft] = useState(asset)
  return (
    <Backdrop onClick={onClose}>
      <ModalCard onClick={e => e.stopPropagation()}>
        <ModalHead>
          <h3>{asset.id ? 'Edit asset' : 'Add asset'}</h3>
        </ModalHead>
        <FieldGroup>
          <FieldLabel>Type</FieldLabel>
          <SearchableSelect
            value={draft.assetType}
            onChange={val => setDraft(d => ({ ...d, assetType: val }))}
            options={types.map(t => ({ value: t.value, label: t.label }))}
            searchPlaceholder='Search asset types…'
            ariaLabel='Asset type'
          />
        </FieldGroup>
        <FieldGroup>
          <FieldLabel>Name</FieldLabel>
          <TextInput value={draft.name || ''} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
            placeholder='e.g. North stair lift'/>
        </FieldGroup>
        <FieldGroup>
          <FieldLabel>Location</FieldLabel>
          <TextInput value={draft.location || ''} onChange={e => setDraft(d => ({ ...d, location: e.target.value }))}
            placeholder='e.g. Level 3 plant room'/>
        </FieldGroup>
        <FieldGroup>
          <FieldLabel>Description</FieldLabel>
          <TextArea value={draft.description || ''} onChange={e => setDraft(d => ({ ...d, description: e.target.value }))}
            placeholder='Optional — make, model, serial number…' rows={3}/>
        </FieldGroup>
        <ModalActions>
          <SecondaryBtn onClick={onClose}>Cancel</SecondaryBtn>
          <PrimaryBtn onClick={() => onSave({
            asset_type: draft.assetType,
            name: draft.name,
            location: draft.location || null,
            description: draft.description || null,
          })} disabled={!draft.name}>Save</PrimaryBtn>
        </ModalActions>
      </ModalCard>
    </Backdrop>
  )
}

export default AssetRegisterPage

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`
const Wrap = styled.div` animation: ${fadeIn} 280ms ease both; `

const Head = styled.header`
  display: flex; align-items: flex-start; justify-content: space-between; gap: 16px;
  margin-bottom: 18px;
  flex-wrap: wrap;
`
const HeadText = styled.div` display: flex; flex-direction: column; gap: 4px; `
const HeadActions = styled.div` display: inline-flex; gap: 8px; `
const Eyebrow = styled.span`
  font-size: 10.5px; letter-spacing: 0.14em; text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]}; font-weight: 800;
`
const Title = styled.h2`
  margin: 0;
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: 24px;
  letter-spacing: -0.5px;
  color: ${({ theme }) => theme.colors.haiti};
`
const Subtitle = styled.p`
  margin: 0;
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.grey[800]};
  line-height: 1.5;
  max-width: 60ch;
`
const ExplainerRow = styled.div` margin-top: 8px; `

const DetectButton = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 6px;
  padding: 9px 16px;
  background: #33becc;
  color: #fff;
  border: none;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 800;
  cursor: pointer;
  min-height: 40px;
  box-shadow: 0 1px 2px rgba(12, 18, 41, 0.08);
  transition: background 160ms ease;
  &:hover:not(:disabled) { background: #2aa6b3; }
  &:disabled { opacity: 0.65; cursor: wait; }
`
const AddButton = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 6px;
  padding: 9px 16px;
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.haiti};
  border: 1px solid #ececec;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 800;
  cursor: pointer;
  min-height: 40px;
  &:hover { background: #f4f4f6; }
`

const EmptyPanel = styled.div`
  padding: 32px;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 14px;
  text-align: center;
  max-width: 620px;
  margin: 0 auto;
  h3 { margin: 0 0 10px; font-family: 'FuturaPT-Light', system-ui, sans-serif; font-size: 22px; }
  p  { margin: 0 0 8px; font-size: 13px; color: ${({ theme }) => theme.colors.grey[800]}; line-height: 1.6; }
`
const NoResults = styled.div`
  padding: 28px 24px;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 14px;
  text-align: center;
  h3 { margin: 0 0 8px; font-family: 'FuturaPT-Light', system-ui, sans-serif; font-size: 20px; color: ${({ theme }) => theme.colors.haiti}; }
  p  { margin: 0 0 12px; font-size: 13.5px; color: ${({ theme }) => theme.colors.grey[800]}; line-height: 1.6; }
`
const ScanNotice = styled.div`
  display: flex; align-items: center; gap: 12px;
  padding: 12px 16px;
  margin-bottom: 16px;
  background: #f0fafb;
  border: 1px solid #cdeef1;
  border-radius: 12px;
  color: #2f4f49;
  svg { color: #2aa6b3; flex-shrink: 0; }
`
const ScanText = styled.div`
  flex: 1;
  font-size: 12.5px;
  line-height: 1.5;
  strong { color: ${({ theme }) => theme.colors.haiti}; }
`
const RefreshBtn = styled.button`
  font: inherit; flex-shrink: 0;
  padding: 8px 14px;
  border: 1px solid #33becc;
  background: #fff;
  color: #2aa6b3;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
  min-height: 38px;
  &:hover { background: #e9f8fa; }
`

const Toolbar = styled.div`
  display: flex; align-items: center; gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`
const SearchWrap = styled.div`
  display: flex; align-items: center; gap: 8px;
  flex: 1; min-width: 240px;
  padding: 0 12px;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 999px;
  min-height: 44px;
  color: ${({ theme }) => theme.colors.grey[800]};
  &:focus-within { border-color: #33becc; box-shadow: 0 0 0 3px rgba(51,190,204,0.12); }
  svg { color: ${({ theme }) => theme.colors.grey[800]}; flex-shrink: 0; }
`
const SearchInput = styled.input`
  font: inherit;
  flex: 1; min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.haiti};
  &::placeholder { color: ${({ theme }) => theme.colors.grey[800]}; }
`
const ClearSearch = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; justify-content: center;
  width: 28px; height: 28px;
  border: none;
  background: transparent;
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.grey[800]};
  cursor: pointer;
  flex-shrink: 0;
  &:hover { background: #f4f4f6; color: ${({ theme }) => theme.colors.haiti}; }
`

const FilterRow = styled.div`
  display: flex; align-items: center; gap: 10px;
  margin-bottom: 8px;
  flex-wrap: wrap;
`
const FilterLabel = styled.span`
  font-size: 10.5px; letter-spacing: 0.10em; text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]}; font-weight: 800;
  min-width: 74px;
`
const Chips = styled.div` display: flex; flex-wrap: wrap; gap: 6px; `
const Chip = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 6px;
  padding: 7px 12px;
  min-height: 34px;
  border-radius: 999px;
  border: 1px solid ${({ $active }) => ($active ? '#33becc' : '#ececec')};
  background: ${({ $active }) => ($active ? '#e6f7f9' : '#fff')};
  color: ${({ $active, theme }) => ($active ? '#2aa6b3' : theme.colors.haiti)};
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
  transition: background 140ms ease, border-color 140ms ease;
  &:hover { border-color: #33becc; }
`
const ChipCount = styled.span`
  font-size: 11px;
  font-weight: 800;
  padding: 1px 7px;
  border-radius: 999px;
  background: rgba(12, 18, 41, 0.06);
  color: inherit;
`

const CountRow = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  margin: 14px 0 10px;
  flex-wrap: wrap;
`
const CountText = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey[800]};
  strong { color: ${({ theme }) => theme.colors.haiti}; font-variant-numeric: tabular-nums; }
`
const ClearFilters = styled.button`
  font: inherit;
  padding: 7px 14px;
  border: 1px solid #ececec;
  background: #fff;
  color: ${({ theme }) => theme.colors.haiti};
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
  min-height: 38px;
  &:hover { background: #f4f4f6; }
`

const TableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
  border: 1px solid #ececec;
  border-radius: 14px;
  background: #fff;
`
const Table = styled.table`
  width: 100%;
  min-width: 720px;
  border-collapse: collapse;
`
const Th = styled.th`
  text-align: ${({ $align }) => $align === 'right' ? 'right' : $align === 'center' ? 'center' : 'left'};
  padding: 12px 16px;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.grey[800]};
  background: #fafafb;
  border-bottom: 1px solid #ececec;
  white-space: nowrap;
  user-select: none;
  cursor: ${({ onClick }) => (onClick ? 'pointer' : 'default')};
  &:hover { color: ${({ onClick, theme }) => (onClick ? theme.colors.haiti : theme.colors.grey[800])}; }
`
const ThInner = styled.span`
  display: inline-flex; align-items: center; gap: 6px;
  justify-content: ${({ $align }) => $align === 'center' ? 'center' : 'flex-start'};
`
const SortIdle = styled.span`
  font-size: 10px;
  opacity: 0.4;
`
const SortActive = styled.span`
  font-size: 9px;
  color: #2aa6b3;
`
const Row = styled.tr`
  border-bottom: 1px solid #f0f0f2;
  &:last-child { border-bottom: none; }
  &:hover { background: #fafcfd; }
`
const Td = styled.td`
  padding: 12px 16px;
  vertical-align: middle;
  text-align: ${({ $align }) => $align === 'right' ? 'right' : $align === 'center' ? 'center' : 'left'};
`
const TypeCell = styled.div` display: inline-flex; align-items: center; gap: 10px; `
const TypeTile = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px;
  border-radius: 8px;
  flex-shrink: 0;
  background: ${({ $tint }) => `${$tint}1A`};
  color: ${({ $tint }) => $tint};
  border: 1px solid ${({ $tint }) => `${$tint}33`};
`
const TypeLabel = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
  white-space: nowrap;
`
const NameCell = styled.div` display: flex; flex-direction: column; gap: 2px; min-width: 0; `
const NameText = styled.span`
  font-size: 14.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
  line-height: 1.3;
`
const DescText = styled.span`
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.grey[800]};
  line-height: 1.4;
  max-width: 42ch;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
const LocationText = styled.span`
  font-size: 13.5px;
  color: ${({ theme }) => theme.colors.haiti};
`
const Muted = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-style: italic;
`
const DateText = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`
const FileChip = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 11px;
  font-weight: 700;
  padding: 4px 9px;
  border-radius: 999px;
  white-space: nowrap;
  background: ${({ $none }) => ($none ? '#fef3c7' : '#f4f4f6')};
  color: ${({ $none, theme }) => ($none ? '#d97706' : theme.colors.grey[800])};
`
const RowActions = styled.div` display: inline-flex; gap: 4px; justify-content: flex-end; `
const IconBtn = styled.button`
  font: inherit;
  width: 36px; height: 36px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.grey[800]};
  cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  &:hover { background: #f0f0f2; color: ${({ theme }) => theme.colors.haiti}; }
`

const pulse = keyframes`
  0%, 100% { opacity: 0.55; }
  50%      { opacity: 1; }
`
const skeletonIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`
const bar = css`
  background: #e7e9ee;
  border-radius: 6px;
  animation: ${pulse} 1.3s ease-in-out infinite;
`
const SkeletonWrap = styled.div` display: flex; flex-direction: column; gap: 12px; `
const SkeletonToolbar = styled.div` display: flex; `
const SkeletonChips = styled.div` display: flex; gap: 6px; flex-wrap: wrap; `
const SkeletonTableBox = styled.div`
  border: 1px solid #ececec;
  border-radius: 14px;
  background: #fff;
  overflow: hidden;
`
const SkeletonRow = styled.div`
  display: flex; align-items: center; gap: 24px;
  padding: 14px 16px;
  border-bottom: 1px solid #f0f0f2;
  animation: ${skeletonIn} 0.4s ease both;
  &:last-child { border-bottom: none; }
`
const SkeletonBar = styled.span`
  ${bar}
  display: inline-block;
  width: ${({ $w }) => $w || '100%'};
  height: ${({ $h }) => $h || '12px'};
  border-radius: ${({ $r }) => $r || '6px'};
`

const Backdrop = styled.div`
  position: fixed; inset: 0;
  background: rgba(12, 18, 41, 0.45);
  display: flex; align-items: center; justify-content: center;
  z-index: 200;
  animation: ${fadeIn} 180ms ease both;
`
const ModalCard = styled.div`
  background: #fff;
  border-radius: 18px;
  padding: 24px 26px;
  width: min(480px, calc(100vw - 32px));
  display: flex; flex-direction: column; gap: 14px;
  box-shadow: 0 18px 48px rgba(12, 18, 41, 0.28);
`
const ModalHead = styled.header`
  h3 { margin: 0; font-family: 'FuturaPT-Light', system-ui, sans-serif; font-size: 22px; color: ${({ theme }) => theme.colors.haiti}; }
`
const FieldGroup = styled.div` display: flex; flex-direction: column; gap: 4px; `
const FieldLabel = styled.label`
  font-size: 10.5px; letter-spacing: 0.10em; text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]}; font-weight: 800;
`
const TextInput = styled.input`
  font: inherit;
  padding: 9px 12px;
  border: 1px solid #ececec;
  border-radius: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};
  outline: none;
  &:focus { border-color: #33becc; box-shadow: 0 0 0 3px rgba(51,190,204,0.12); }
`
const TextArea = styled.textarea`
  font: inherit;
  padding: 9px 12px;
  border: 1px solid #ececec;
  border-radius: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};
  resize: vertical;
  outline: none;
  &:focus { border-color: #33becc; box-shadow: 0 0 0 3px rgba(51,190,204,0.12); }
`
const ModalActions = styled.div`
  display: flex; gap: 8px; justify-content: flex-end;
  margin-top: 4px;
`
const SecondaryBtn = styled.button`
  font: inherit;
  padding: 9px 16px;
  background: transparent;
  border: 1px solid #ececec;
  color: ${({ theme }) => theme.colors.haiti};
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
  &:hover { background: #f4f4f6; }
`
const PrimaryBtn = styled.button`
  font: inherit;
  padding: 9px 18px;
  background: #33becc;
  border: none;
  color: #fff;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 800;
  cursor: pointer;
  transition: background 160ms ease;
  &:hover:not(:disabled) { background: #2aa6b3; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`
